'use client';

import { FC } from 'react';
import { motion } from 'framer-motion';
import ProjectCard from '@/components/cards/ProjectCard';
import Link from 'next/link';
import { FiBox, FiArrowRight } from 'react-icons/fi';

interface ProjectsSectionProps {
  projects: any[];
}

const ProjectsSection: FC<ProjectsSectionProps> = ({ projects }) => {
  // Check if we actually have data to render
  const hasProjects = projects && projects.length > 0;

  return (
    <section className="py-32 max-w-7xl mx-auto px-4 sm:px-8 relative">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 -translate-y-1/2 w-96 h-96 bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none" />

      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 md:mb-20 gap-8">
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-3"
          >
            <span className="w-12 h-[1px] bg-indigo-500" />
            <span className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-600 dark:text-indigo-400">
              Technical_Showcase
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-black tracking-tighter text-zinc-900 dark:text-white uppercase leading-none"
          >
            Engineering <br />
            <span className="text-zinc-300 dark:text-zinc-800">Laboratory</span>
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="max-w-md border-l-2 border-zinc-100 dark:border-zinc-900 pl-8 py-2"
        >
          <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed font-medium">
            A curated selection of verified deployments, focusing on distributed systems, 
            low-latency architectures, and strategic academic research.
          </p>
        </motion.div>
      </div>

      {/* Projects Grid / Empty State */}
      {hasProjects ? (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 auto-rows-fr"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
          }}
        >
          {projects.map((project) => (
            <motion.div
              key={project.id}
              variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="min-h-[500px]"
            >
              <ProjectCard project={project} />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        /* Tactical Empty State for Build-time or Zero-data */
        <div className="py-24 border-2 border-dashed border-zinc-100 dark:border-zinc-900 rounded-[3rem] flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-zinc-50 dark:bg-zinc-900 rounded-2xl flex items-center justify-center mb-6 border border-zinc-100 dark:border-zinc-800">
            <FiBox className="text-zinc-300 dark:text-zinc-700" size={24} />
          </div>
          <h3 className="text-xs font-black uppercase tracking-[0.3em] text-zinc-400 mb-2">Registry Offline</h3>
          <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Awaiting tactical data feed initialization...</p>
        </div>
      )}

      {/* CTA Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        className="mt-20 flex flex-col items-center gap-6"
      >
        <div className="h-px w-24 bg-zinc-200 dark:bg-zinc-800" />

        <Link
          href="/projects"
          className="group relative inline-flex items-center gap-4 px-10 py-5 rounded-[2rem] border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-xs font-black uppercase tracking-[0.3em] text-zinc-900 dark:text-white hover:border-indigo-500 transition-all duration-500 shadow-xl shadow-transparent hover:shadow-indigo-500/10"
        >
          <span>Access Full Archive</span>
          <span className="text-indigo-500 group-hover:translate-x-1 transition-transform">
             <FiArrowRight size={18} />
          </span>
        </Link>
      </motion.div>
    </section>
  );
};

export default ProjectsSection;